import { Auth } from '@aws-amplify/auth';
import { createApiClient } from 'src/helpers/api-client';
import { UsersApi } from '@/openapi/apis/UsersApi';

/**
 * @typedef {{
 *    sub: string,
 *    email: string,
 *    forceChangePW: boolean,
 *    privilege?: string,
 *    tenantId?: number, 
 *    firstName?: string, 
 *    lastName?: string,
 *    lastSignedInAt?: Date,
 *  }} AuthUser
 * @typedef {import('@aws-amplify/auth').CognitoUser & { attributes: { 
 *    sub: string,
 *    email: string,
 *    'custom:force_change_pw': string,
 *  } }} CognitoUser
 * @typedef {import('@aws-amplify/auth/lib-esm/types').CurrentUserOpts & { bypassCacheUserInfo: boolean }} CurrentUserOpts
 * @typedef {import('@aws-amplify/auth/lib-esm/types').SignOutOpts} SignOutOpts
 */

/**
 * @param {CurrentUserOpts} params 
 * @returns {Promise<CognitoUser>}
 */
async function currentAuthenticatedUser(params) {
  return await Auth.currentAuthenticatedUser(params);
}

class AuthService {
  authUserLocalStorageKey = 'authUser';
  oAuthFlowInProgressLocalStorageKey = 'oAuthFlowInProgress';

  defaultSessionDuration = 1 * 24 * 60 * 60 * 1000; // 1 day
  signageSessionDuration = 14 * 24 * 60 * 60 * 1000; // 14 days for signage users

  /**
   * @returns {AuthUser | null}
   */
  _localAuthUser() {
    try {
      const authUserJson = localStorage.getItem(this.authUserLocalStorageKey);
      if (!authUserJson) {
        return null;
      }
      const authUser = JSON.parse(authUserJson);
      if (authUser?.lastSignedInAt) {
        authUser.lastSignedInAt = new Date(authUser.lastSignedInAt);
      }

      let sessionDuration = this.defaultSessionDuration;
      if (authUser.privilege === 'signage') {
        sessionDuration = this.signageSessionDuration
      }
      if (!authUser.lastSignedInAt || (new Date().getTime() - authUser.lastSignedInAt.getTime()) >= sessionDuration) {
        this.signOut();
        return null;
      }

      return authUser;
    } catch (error) {
      console.error('Error retrieving local auth user:', error);
      return null;
    }
  }

  /**
   * @param {CurrentUserOpts} params 
   * @returns 
   */
  async _getIdToken(params = undefined) {
    try {
      const cognitoUser = await currentAuthenticatedUser(params);
      return cognitoUser.getSignInUserSession().getIdToken().getJwtToken();
    } catch (error) {
      return null
    }
  }

  /**
   * @param {CurrentUserOpts} params 
   * @returns 
   */
  async idToken(params = undefined) {
    await this.currentUser();
    return await this._getIdToken(params);
  }

  /**
   * @returns {Promise<AuthUser | null>}
   */
  async _fetchUserInfo() {
    /**
     * @type {AuthUser}
     */
    const authUser = {
      lastSignedInAt: this._localAuthUser()?.lastSignedInAt || new Date().toISOString(),
    }

    const cognitoUser = await currentAuthenticatedUser();
    authUser.sub = cognitoUser.attributes.sub;
    authUser.email = cognitoUser.attributes.email;
    authUser.forceChangePW = cognitoUser.attributes['custom:force_change_pw'] === 'true'
    if (authUser.forceChangePW) {
      localStorage.setItem(this.authUserLocalStorageKey, JSON.stringify(authUser));
      return authUser;
    }

    let usersApi = createApiClient(UsersApi);
    usersApi = usersApi.withPreMiddleware(async context => {
      context.init.headers = {
        ...context.init.headers,
        'Authorization': `Bearer ${await this._getIdToken()}`
      };
      return context;
    })
    const userInfo = await usersApi.getUserByEmail({
      email: authUser.email
    }).catch(async error => {
      await this.signOut();
      throw error;
    });
    authUser.privilege = userInfo?.privilege;
    authUser.tenantId = userInfo?.tenantId;
    authUser.firstName = userInfo?.firstname;
    authUser.lastName = userInfo?.lastname;

    localStorage.setItem(this.authUserLocalStorageKey, JSON.stringify(authUser));
    return authUser;
  }

  /**
   * @param {CurrentUserOpts} params
   * @returns {Promise<AuthUser | null>}
   */
  async currentUser(params = undefined) {
    let cognitoUser;
    try {
      cognitoUser = await currentAuthenticatedUser(params);
    } catch (error) {
      await this.signOut();
      return null;
    }

    let authUser;
    const oAuthFlowInProgress = localStorage.getItem(this.oAuthFlowInProgressLocalStorageKey);
    if (oAuthFlowInProgress !== 'true') {
      authUser = this._localAuthUser();
      if (!authUser) {
        await this.signOut();
        return null;
      }
    }

    if (oAuthFlowInProgress === 'true') {
      authUser = await this._fetchUserInfo();
      localStorage.removeItem(this.oAuthFlowInProgressLocalStorageKey);
    } else {
      if (!cognitoUser.attributes.sub || authUser.sub !== cognitoUser.attributes.sub) {
        await this.signOut();
        return null;
      }

      if (params?.bypassCacheUserInfo) {
        authUser = await this._fetchUserInfo();
      }
    }

    return authUser;
  }

  /**
   * @returns {Promise<AuthUser>}
   */
  async signIn(username, password) {
    await Auth.signIn(username, password);
    return await this._fetchUserInfo();
  }

  /**
   * @returns {Promise<AuthUser>}
   */
  async signInWithIdp(provider) {
    localStorage.setItem(this.oAuthFlowInProgressLocalStorageKey, 'true');
    try {
      await Auth.federatedSignIn({ provider });
    } catch (error) {
      localStorage.del(this.oAuthFlowInProgressLocalStorageKey);
    }
  }

  /**
   * 
   * @param {SignOutOpts} opts 
   */
  async signOut(opts = undefined) {
    const result = await Auth.signOut(opts);
    localStorage.removeItem(this.authUserLocalStorageKey);
    return result;
  }

  /**
   * @param {string} newPassword 
   * @returns {Promise<AuthUser | null>}
   */
  async completeNewPasswordChallenge(newPassword) {
    const cognitoUser = await currentAuthenticatedUser();
    const username = cognitoUser.getUsername();

    const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/users/complete-new-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await this._getIdToken()}`
      },
      body: JSON.stringify({
        password: newPassword
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to complete password setup' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    await Auth.signOut();
    return this.signIn(username, newPassword);
  }

  async resetPassword(email) {
    return await Auth.forgotPassword(email);
  }

  async confirmResetPassword(email, code, newPassword) {
    const result = await Auth.forgotPasswordSubmit(email, code, newPassword);
    /**
     * @type {CognitoUser}
     */
    const cognitoUser = await Auth.signIn(email, newPassword);

    // Set forceChangePW to false after successful password reset for the first login
    if (cognitoUser.attributes['custom:force_change_pw'] === 'true') {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/users/complete-new-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cognitoUser.getSignInUserSession().getIdToken().getJwtToken()}`
        },
        body: JSON.stringify({
          password: newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to complete password setup' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    }

    await Auth.signOut();
    return result;
  }
}

export default new AuthService(); 
